import { useState } from 'react'
import { Link } from 'react-router'
import { useNavigate, useLocation, useOutletContext } from 'react-router'
import { FaArrowLeft } from 'react-icons/fa'
import { IoBalloonOutline, IoCalendarOutline } from 'react-icons/io5'
import clsx from 'clsx'

import { useAppSelector } from '../../hooks/redux'
import { CurrentUser } from '../../services/schema/User'
import { useGetUserWithUserNameQuery } from '../../services/apiSlice'
import { useUpdateUserFollowsMutation } from '../../services/apiSlice'
import UserLimits from '../../components/user/UserLimits'
import PostsByUser from '../../components/post/PostsByUser'
import Filler from '../../components/utils/Filler'
import Spinner from '../../components/loading/Spinner'
import NotFound from '../../components/fallback/NotFound'

const tabs = [
  { title: 'Posts', id: 0 },
  { title: 'Replies', id: 1 },
  { title: 'Likes', id: 2 },
]

const User: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useOutletContext<CurrentUser>()
  const prevLocUrl = useAppSelector((state) => state.app.prevLocation.url)
  const [updateUserFollows] = useUpdateUserFollowsMutation()
  const [selectedTab, setSelectedTab] = useState(tabs[0])

  const slicedPathname = location.pathname.slice(1)

  const { data, isError } = useGetUserWithUserNameQuery(
    {
      username: slicedPathname,
    },
    { skip: slicedPathname === user.username },
  )

  let main = (
    <>
      <div className="relative flex w-full grow">
        <div className="h-dvh" />
        <div className="border-theme bgd-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l md:mr-0">
          <Filler />
          <div className="border-theme sticky top-0 left-0 z-[100] flex h-[60px] w-full items-center border-b bg-inherit pl-[10px] text-xl font-bold">
            <button
              onClick={() => {
                navigate('/home', { replace: true })
              }}
              className="hover-main flex h-[40px] w-[40px] items-center justify-center rounded-full text-sm"
            >
              <FaArrowLeft />
            </button>
            <p className="pl-[20px] tracking-wide">Loading...</p>
          </div>
          <div className="relative flex flex-col items-center pt-[150px]">
            <Spinner />
          </div>
        </div>
      </div>
    </>
  )

  const userToShow = user.username !== slicedPathname ? data : user

  if (isError) {
    return (
      <>
        <NotFound />
      </>
    )
  }

  if (userToShow) {
    const profilePic =
      userToShow.profilePicture !== null
        ? userToShow.profilePicture
        : '/user_sample.jpg'
    const headerPicture = userToShow.headerPicture
      ? userToShow.headerPicture
      : null

    const followedByUser =
      userToShow.username !== user.username &&
      Array.isArray(user.userFollowsCount.following) &&
      user.userFollowsCount.following.includes(userToShow.username)

    main = (
      <>
        <div className="relative flex w-full grow">
          <div className="h-dvh" />
          <div className="user-container border-theme bgd-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l md:mr-0">
            <Filler />
            <div className="border-theme sticky top-0 left-0 z-[80] flex h-[60px] w-full items-center border-b bg-inherit pl-[10px] text-xl font-bold">
              <button
                onClick={() => {
                  navigate('/home', { replace: true })
                }}
                className="hover-main flex h-[40px] w-[40px] items-center justify-center rounded-full text-sm"
              >
                <FaArrowLeft />
              </button>
              <p className="pl-[20px] tracking-wide">
                {userToShow.displayName}
              </p>
            </div>
            <div className="relative flex flex-col">
              <div
                className="h-[200px] w-full cursor-pointer"
                style={{
                  backgroundImage: headerPicture ? `url(${headerPicture})` : '',
                  backgroundColor: !headerPicture ? 'gray' : '',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                onClick={() => {
                  navigate(`/media/headerPicture?user=${userToShow.username}`)
                }}
              />
              <div className="relative h-auto bg-transparent">
                <div
                  className="border-theme absolute top-[-65px] left-[15px] h-[130px] w-[130px] cursor-pointer rounded-full border-4 border-solid"
                  style={{
                    backgroundImage: `url(${profilePic})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                  onClick={() => {
                    navigate(
                      `/media/profilePicture?user=${userToShow.username}`,
                    )
                  }}
                />
                <div className="mr-[20px] flex h-[70px] items-center justify-end">
                  {userToShow.username === user.username && (
                    <Link
                      className="border-theme hover-main flex h-auto w-auto rounded-full border px-[20px] py-[5px] font-bold"
                      to="/settings/profile"
                    >
                      Edit profile
                    </Link>
                  )}
                  {userToShow.username !== user.username &&
                    userToShow.verified && (
                      <button
                        type="button"
                        className="border-theme hover-main flex h-auto w-auto rounded-full border px-[20px] py-[5px] font-bold"
                        onClick={() => {
                          if (followedByUser) {
                            if (confirm('Are you sure you want to unfollow?')) {
                              updateUserFollows({
                                type: 'unfollow',
                                currUser: user.username,
                                otherUser: userToShow.username as string,
                              })
                              return
                            }
                          }
                          updateUserFollows({
                            type: 'follow',
                            currUser: user.username,
                            otherUser: userToShow.username as string,
                          })
                        }}
                      >
                        {followedByUser ? 'Unfollow' : 'Follow'}
                      </button>
                    )}
                  {!userToShow.verified && (
                    <h1 className="text-red pl-[25px] text-sm font-semibold underline">
                      User's email is unverified!
                    </h1>
                  )}
                </div>
                <div className="h-auto w-full px-[20px]">
                  <div className="mb-[10px] flex flex-col">
                    <div className="flex w-full">
                      <h1 className="text-xl font-bold">
                        {userToShow.displayName}
                      </h1>
                      {
                        // <button
                        //   className="border-theme hover-main ml-[20px] cursor-pointer rounded-full border px-[20px] text-sm font-medium"
                        //   type="button"
                        // >
                        //   verify email
                        // </button>
                      }
                    </div>
                    <h1 className="text-sub">@{userToShow.username}</h1>
                  </div>

                  <div className="block h-auto w-full break-words">
                    <p>{userToShow.bioText ? userToShow.bioText : ''}</p>
                  </div>
                  <div className="text-sub flex w-full items-center py-[10px] font-semibold">
                    <IoBalloonOutline />
                    <p className="ml-[5px]">Born</p>
                    <p className="ml-[5px]">{userToShow.dates.dateOfBirth}</p>
                    <IoCalendarOutline className="ml-[10px]" />
                    <p className="ml-[5px]">Joined</p>
                    <p className="ml-[5px]">
                      {userToShow.dates.createdAtShort}
                    </p>
                  </div>
                  <div className="mb-[15px] flex w-full text-base">
                    <Link to="following" className="ul-link flex flex-col">
                      <p className="flex">
                        {userToShow.userFollowsCount.followingCount}
                        <span className="text-sub pl-[3px]"> Following</span>
                      </p>
                      <div className="ul-line"></div>
                    </Link>
                    <Link
                      to="followers"
                      className="ul-link ml-[25px] flex flex-col"
                    >
                      <p className="flex">
                        {userToShow.userFollowsCount.followersCount}
                        <span className="text-sub pl-[3px]"> Followers</span>
                      </p>
                      <div className="ul-line"></div>
                    </Link>
                  </div>
                  {userToShow.username === user.username && (
                    <UserLimits
                      userLimits={{
                        postCount: user.postCount,
                        replyCount: user.replyCount,
                        userInfoChangeCount: user.userInfoChangeCount,
                      }}
                    />
                  )}
                </div>
              </div>
              {/* ROWS - TODO:REFACTOR-same with home header */}
              <div className="border-theme flex h-[50px] border-b bg-transparent">
                {tabs.map((tab, i) => {
                  return (
                    <div
                      key={tab.title + i}
                      className="hover-main relative flex h-full flex-1 items-center justify-center px-[20px]"
                      onClick={() => setSelectedTab(tab)}
                    >
                      <div className="relative flex h-full items-center justify-center px-[10px] font-semibold tracking-tight">
                        <p
                          className={clsx(
                            'flex w-full items-center text-nowrap',
                            {
                              'text-main': selectedTab.id === tab.id,
                              'text-sub': selectedTab.id !== tab.id,
                            },
                          )}
                        >
                          {tab.title}
                        </p>
                        {selectedTab.id === tab.id && (
                          <div className="bgd-blue absolute bottom-0 h-[3px] w-full"></div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            {selectedTab.title === 'Posts' ? (
              <PostsByUser username={slicedPathname} />
            ) : (
              <div className="text-sub flex h-[200px] w-full items-center justify-center px-[50px]">
                <h1>{`'${selectedTab.title}' tab is not yet an available feature in this version of the app.`}</h1>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  return <>{main}</>
}

export default User
